import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { type AuthUser } from "wasp/auth";
import {
  ArrowLeft,
  User as UserIcon,
  Mail,
  Shield,
  CreditCard,
  Trash2,
  Edit3,
  MapPin,
  Award,
  TrendingUp,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";
import { useQuery, useAction, updateIsUserAdminById } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { type User } from "wasp/entities";
import { getUserById, deleteUserById } from "wasp/client/operations";
import { toast } from "../../../hooks/use-toast";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonDanger,
  DetailField,
} from "../../../components/layout";

interface UserDetailPageProps {
  user: AuthUser;
}

function AdminSwitch({ id, isAdmin }: Pick<User, "id" | "isAdmin">) {
  const { data: currentUser } = useAuth();
  const isCurrentUser = currentUser?.id === id;

  return (
    <Switch
      checked={isAdmin}
      onCheckedChange={(value) =>
        updateIsUserAdminById({ id: id, isAdmin: value })
      }
      disabled={isCurrentUser}
    />
  );
}

const UserDetailPage = ({ user: _user }: UserDetailPageProps) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const deleteUserAction = useAction(deleteUserById);

  // Fetch user details
  const { data: currentUser, isLoading } = useQuery(getUserById, {
    userId: userId || "",
  });

  const handleBack = () => {
    navigate("/admin/users");
  };

  const handleEdit = () => {
    navigate(`/admin/users/${userId}/edit`);
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    ) {
      if (!userId) return;
      try {
        await deleteUserAction({ userId });
        toast({
          title: "Success",
          description: "User successfully deleted.",
        });
        navigate("/admin/users");
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to delete user.",
          variant: "destructive",
        });
      }
    }
  };

  if (isLoading) {
    return (
      <IndividualViewPageLayout layoutVariant="admin" authUser={_user}>
        <div className="flex justify-center items-center py-12">
          <div className="text-center">Loading...</div>
        </div>
      </IndividualViewPageLayout>
    );
  }

  if (!currentUser) {
    return (
      <IndividualViewPageLayout layoutVariant="admin" authUser={_user}>
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              User Not Found
            </h2>
            <p className="text-gray-600 mb-4">
              The user you're looking for doesn't exist.
            </p>
            <Button onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Users
            </Button>
          </div>
        </div>
      </IndividualViewPageLayout>
    );
  }

  const approvalScore = currentUser.approvalScore || 0;
  const scoreColor =
    approvalScore >= 80
      ? "text-green-600"
      : approvalScore >= 60
        ? "text-yellow-600"
        : "text-red-600";

  return (
    <IndividualViewPageLayout
      layoutVariant="admin"
      authUser={_user}
      sidebarContent={
        <>
          <SidebarSection isFirst={true}>
            <ActionButtons>
              <ActionButton onClick={handleEdit} icon={Edit3}>
                Edit User
              </ActionButton>
              <ActionButtonDanger onClick={handleDelete} icon={Trash2}>
                Delete User
              </ActionButtonDanger>
            </ActionButtons>
          </SidebarSection>

          <SidebarSection title="User Statistics">
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Points Earned
                </span>
                <span className="font-medium text-lg">
                  {currentUser.pointsEarned || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Approval Score
                </span>
                <span className={`font-medium text-lg ${scoreColor}`}>
                  {approvalScore}%
                </span>
              </div>
              <div className="border-t pt-3 mt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Approved
                  </span>
                  <span className="font-medium text-green-600">
                    {currentUser.totalPostsApproved || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                    Rejected
                  </span>
                  <span className="font-medium text-red-600">
                    {currentUser.totalPostsRejected || 0}
                  </span>
                </div>
              </div>
            </div>
          </SidebarSection>

          <SidebarSection title="Account Info">
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">User ID:</span>
                <p className="font-mono text-xs break-all mt-1">
                  {currentUser.id}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>
                <p className="font-medium mt-1">
                  {currentUser.createdAt
                    ? new Date(currentUser.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </SidebarSection>
        </>
      }
    >
      {/* Unified Grid Layout */}
      <div className="border border-gray-200 dark:border-gray-700">
        <div className="grid" style={{ gridTemplateColumns: "30% 70%" }}>
          {/* User Information Section */}
          <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              USER INFORMATION
            </h3>
          </div>

          <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Display Name
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm text-gray-900 dark:text-white">
              {currentUser.username || "Not set"}
            </div>
          </div>

          <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Username
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm text-gray-900 dark:text-white">
              {currentUser.username || "Not set"}
            </div>
          </div>

          <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Email
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm text-gray-900 dark:text-white">
              {currentUser.email}
            </div>
          </div>

          <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Post Approval
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm flex items-center gap-2 font-medium">
              {currentUser.requiresApproval ? (
                <span className="flex items-center gap-1 text-yellow-600">
                  <Shield className="w-4 h-4" />
                  Requires Approval
                </span>
              ) : (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  Auto-Approved
                </span>
              )}
            </div>
          </div>

          {/* Locations Section */}
          <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              LOCATIONS
            </h3>
          </div>

          <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Primary
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm text-gray-900 dark:text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              {currentUser.primaryLocation?.name || "No primary location set"}
            </div>
          </div>

          <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Role
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm text-gray-900 dark:text-white">
              {currentUser.isAdmin ? "Admin" : currentUser.isModerator ? "Moderator" : "User"}
            </div>
          </div>

          <div className="border-r border-gray-200 dark:border-gray-700 py-3 px-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Subscribed Locations
            </div>
          </div>
          <div className="py-3 px-4">
            <div className="text-sm text-gray-900 dark:text-white">
              {currentUser.locationMemberships &&
                currentUser.locationMemberships.length > 0 ? (
                (() => {
                  const subscribedMemberships =
                    currentUser.locationMemberships.filter(
                      (membership: any) =>
                        membership.locationId !==
                        currentUser.primaryLocationId &&
                        membership.isSubscribed,
                    );
                  return subscribedMemberships.length > 0 ? (
                    <div className="space-y-2">
                      {subscribedMemberships.map((membership: any) => (
                        <div
                          key={membership.id}
                          className="p-2 bg-gray-50 dark:bg-gray-800 rounded"
                        >
                          <p className="font-medium text-sm">
                            {membership.location?.name || membership.locationId}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No additional subscribed locations
                    </p>
                  );
                })()
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No subscribed locations
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </IndividualViewPageLayout >
  );
};

export default UserDetailPage;
